import { useEffect, useState, type RefObject } from 'react';

interface Options {
  /** Nombre d'éléments visibles en une colonne (mobile). */
  mobile?: number;
  /** Nombre de rangées visibles au-delà d'une colonne (tablette/desktop). */
  rows?: number;
}

/**
 * Nombre d'éléments à afficher dans une grille fluide, **adapté à l'écran** : on mesure le nombre
 * réel de colonnes rendues (les grilles sont en `auto-fill`, donc variables), puis on borne à
 * `rows` rangées. Sur une seule colonne (mobile), on affiche `mobile` éléments — sinon la limite
 * « 3 rangées » n'en montrerait que 3.
 *
 * La mesure lit `grid-template-columns` calculé (le nombre de pistes) et se rafraîchit au
 * redimensionnement via un `ResizeObserver`. Le reste des éléments passe derrière une carte
 * « Autres… » côté appelant.
 */
export function useVisibleCount(
  gridRef: RefObject<HTMLElement | null>,
  { mobile = 5, rows = 3 }: Options = {},
): number {
  const [count, setCount] = useState(mobile);

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;

    const measure = () => {
      const template = getComputedStyle(el).gridTemplateColumns;
      const columns = template.split(' ').filter((t) => t.trim().length > 0).length || 1;
      setCount(columns <= 1 ? mobile : columns * rows);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [gridRef, mobile, rows]);

  return count;
}
